<template>
    <section class="scroll-img-section">
        <div class="scroll-img-section__container">
            <CardTitle v-if="!hideTitle && item.mainTitle" :module="item" />

            <div class="scroll-img__viewport" @mouseenter="pauseAuto" @mouseleave="resumeAuto"
                @touchstart.passive="pauseAuto" @touchend.passive="resumeAuto">
                <button class="scroll-img__arrow scroll-img__arrow--left" @click="scrollPrev"
                    :aria-label="t('common.prev')">
                    <ChevronLeft />
                </button>

                <div class="scroll-img__track-wrap">
                    <div ref="trackRef" class="scroll-img__track" :style="trackStyle">
                        <div v-for="(cell, idx) in renderList" :key="genKey(cell, idx)" class="scroll-img__card"
                            @click="handleCellClick(cell, idx)">
                            <MediaCard v-if="getCellImage(cell)" class="scroll-img__visual" :url="getCellImage(cell)"
                                :name="getCellTitle(cell)" video-popup />
                            <div v-if="getCellTitle(cell) || getCellDesc(cell)" class="scroll-img__text">
                                <h4 v-if="getCellTitle(cell)" class="scroll-img__title">{{ getCellTitle(cell) }}</h4>
                                <p v-if="getCellDesc(cell)" class="scroll-img__desc">{{ getCellDesc(cell) }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <button class="scroll-img__arrow scroll-img__arrow--right" @click="scrollNext"
                    :aria-label="t('common.next')">
                    <ChevronRight />
                </button>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, onActivated, onDeactivated } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Module } from '@/api/page_config'
import CardTitle from './CardTitle.vue'
import ChevronRight from '@/assets/icons/chevron-right.svg'
import ChevronLeft from '@/assets/icons/chevron-left.svg'
import { getStrWithRecord, jumpToUrl } from './index.js'

const { t } = useI18n()

const props = withDefaults(defineProps<{
    item: Module
    hideTitle?: boolean
}>(), { hideTitle: false })

const emit = defineEmits<{
    (e: 'click', item: Module, cell?: unknown): void
}>()

const trackRef = ref<HTMLElement | null>(null)

/** 原始列表 */
const rawList = computed(() => props.item.fieldData || [])
const total = computed(() => rawList.value.length)

/**
 * 渲染列表：复制一份实现无缝循环
 * [...realItems, ...realItems]
 */
const renderList = computed(() => {
    const list = rawList.value
    if (list.length <= 1) return list
    return [...list, ...list]
})

function genKey(cell: unknown, idx: number): string {
    const c = cell as Record<string, unknown>
    return `card-${String(c?.id ?? idx)}-${idx >= total.value ? 'dup' : 'orig'}`
}

function getCellImage(cell: unknown): string {
    return getStrWithRecord(cell, 'image')
}
function getCellTitle(cell: unknown): string {
    return getStrWithRecord(cell, 'title')
}
function getCellDesc(cell: unknown): string {
    return getStrWithRecord(cell, 'subTitle')
}
function getCellUrl(cell: unknown): string {
    return getStrWithRecord(cell, 'url')
}

function handleCellClick(cell: unknown, renderIdx: number): void {
    // 复制项不触发点击
    if (total.value > 1 && renderIdx >= total.value) return
    emit('click', props.item, cell)
    const url = getCellUrl(cell)
    if (url) {
        jumpToUrl(url)
    }
}

/* ============ 无限自动滚动逻辑 ============ */

const AUTO_SPEED = 0.5 // px / frame (约 30px/s)
const CARD_GAP = 16 // 与CSS gap一致 (桌面1.25rem=20，手机1rem=16)
const CARD_GAP_DESKTOP = 20

let rafId: number | null = null
let offset = 0 // 当前translateX偏移（负数）
let paused = false
let singleSetWidth = 0 // 一组（原始列表）的总宽度
let cardWidth = 0
let isManuallyScrolling = false
let manualTimer: ReturnType<typeof setTimeout> | null = null

function getGap(): number {
    return window.innerWidth >= 768 ? CARD_GAP_DESKTOP : CARD_GAP
}

function measure(): void {
    const el = trackRef.value
    if (!el || !el.firstElementChild) return
    cardWidth = (el.firstElementChild as HTMLElement).offsetWidth
    const gap = getGap()
    singleSetWidth = total.value * (cardWidth + gap)
}

function applyTransform(): void {
    if (!trackRef.value) return
    trackRef.value.style.transform = `translateX(${offset}px)`
}

/** 无限循环：越过一组宽度时瞬间重置 */
function checkLoop(): void {
    if (singleSetWidth <= 0) return
    // 向左滚超过一组 → 回到0位置（瞬间，无过渡）
    if (Math.abs(offset) >= singleSetWidth) {
        offset += singleSetWidth
        applyTransform()
    }
    // 向右滚过0（负数偏移）→ 回到一组末尾
    if (offset > 0) {
        offset -= singleSetWidth
        applyTransform()
    }
}

function tick(): void {
    if (paused || isManuallyScrolling) {
        rafId = requestAnimationFrame(tick)
        return
    }
    offset -= AUTO_SPEED
    applyTransform()
    checkLoop()
    rafId = requestAnimationFrame(tick)
}

function startAuto(): void {
    if (total.value <= 1) return
    stopAuto()
    measure()
    rafId = requestAnimationFrame(tick)
}

function stopAuto(): void {
    if (rafId !== null) {
        cancelAnimationFrame(rafId)
        rafId = null
    }
}

function pauseAuto(): void {
    paused = true
}
function resumeAuto(): void {
    paused = false
}

/** 点击按钮时滚动一张卡片宽度 */
function scrollOneCard(direction: 1 | -1): void {
    if (total.value <= 1) return
    isManuallyScrolling = true
    if (manualTimer) clearTimeout(manualTimer)

    measure()
    const step = cardWidth + getGap()
    const targetOffset = offset - direction * step
    const startOffset = offset
    const duration = 400
    const startTime = performance.now()

    const animate = (now: number) => {
        const elapsed = now - startTime
        const progress = Math.min(elapsed / duration, 1)
        // ease-out-cubic
        const eased = 1 - Math.pow(1 - progress, 3)
        offset = startOffset + (targetOffset - startOffset) * eased
        applyTransform()
        checkLoop()
        if (progress < 1) {
            requestAnimationFrame(animate)
        } else {
            manualTimer = setTimeout(() => {
                isManuallyScrolling = false
            }, 100)
        }
    }
    requestAnimationFrame(animate)
}

function scrollNext(): void {
    scrollOneCard(1)
}
function scrollPrev(): void {
    scrollOneCard(-1)
}

const trackStyle = computed(() => ({
    willChange: 'transform'
}))

let resizeObserver: ResizeObserver | null = null

onMounted(() => {
    nextTick(() => {
        measure()
        startAuto()
        // 监听尺寸变化重新测量
        if (trackRef.value) {
            resizeObserver = new ResizeObserver(() => {
                measure()
            })
            resizeObserver.observe(trackRef.value)
        }
    })
})

onUnmounted(() => {
    stopAuto()
    if (manualTimer) clearTimeout(manualTimer)
    if (resizeObserver) resizeObserver.disconnect()
})

onActivated(() => {
    resumeAuto()
})
onDeactivated(() => {
    pauseAuto()
})
</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

$orange: #ff6d00;

.scroll-img-section {
    width: 100%;
}

.scroll-img-section__container {
    max-width: $container-max-width;
    margin: 0 auto;
    padding: 0;

    @media (min-width: $breakpoint-md) {
        padding: 0 2rem;
    }
}

.scroll-img__viewport {
    position: relative;
    padding: 0;
    overflow: hidden;

    // @media (min-width: $breakpoint-md) {
    //     padding: 0 3rem;
    // }
}

.scroll-img__track-wrap {
    overflow: hidden;
    margin: 0 1rem;

    @media (min-width: $breakpoint-md) {
        margin: 0;
    }
}

.scroll-img__track {
    display: flex;
    gap: 1rem;
    padding: 0.5rem 0;
    width: max-content;

    @media (min-width: $breakpoint-md) {
        gap: 1.25rem;
    }
}

.scroll-img__card {
    flex-shrink: 0;
    width: 280px;
    border-radius: 1rem;
    overflow: hidden;
    cursor: pointer;
    position: relative;
    background: $color-bg-secondary;
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    @media (min-width: $breakpoint-md) {
        width: 380px;
        border-radius: 1.25rem;
    }

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 28px rgba(0, 0, 0, 0.1);

        .scroll-img__visual img {
            transform: scale(1.05);
        }
    }
}

.scroll-img__visual {
    position: relative;
    width: 100%;
    // aspect-ratio: 16 / 10;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.4s ease;
        display: block;
    }
}

.scroll-img__text {
    padding: 1rem;
    background: #fff;

    @media (min-width: $breakpoint-md) {
        padding: 1.25rem 1.5rem;
    }
}

.scroll-img__title {
    font-size: 1rem;
    font-weight: 700;
    color: $color-text;
    margin: 0 0 0.25rem;
    line-height: 1.4;

    @media (min-width: $breakpoint-md) {
        font-size: 1.125rem;
    }
}

.scroll-img__desc {
    font-size: 0.8125rem;
    color: $color-text-secondary;
    margin: 0;
    line-height: 1.5;

    @media (min-width: $breakpoint-md) {
        font-size: 0.875rem;
    }
}

/* 左右箭头按钮 */
.scroll-img__arrow {
    position: absolute;
    top: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    z-index: 3;
    color: $color-text;
    transition: all 0.2s ease;
    transform: translateY(-50%);

    @media (max-width: 767px) {
        display: none;
    }

    @media (min-width: $breakpoint-md) {
        width: 48px;
        height: 48px;
    }

    svg {
        width: 22px;
        height: 22px;
    }

    &:hover {
        background: $orange;
        color: #fff;
        box-shadow: 0 6px 16px rgba(255, 109, 0, 0.3);
        transform: translateY(-50%) scale(1.08);
    }

    &--left {
        left: 1rem;
    }

    &--right {
        right: 1rem;
    }
}
</style>
